import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { processKakaoLogin } from '../../services/kakaoAuthService';
import { logError } from '../../utils/errorHandling';
import { CircularProgress, Box } from '@mui/material';
import { supabase } from '../../config/supabase';

const KakaoAuthHandler: React.FC = () => {
  const navigate = useNavigate();
  const isProcessing = useRef(false);
  const processedCode = useRef<string | null>(null);

  useEffect(() => {
    const handleKakaoCallback = async () => {
      try {
        // 이미 처리 중인 경우 중복 실행 방지
        if (isProcessing.current) {
          console.log('Already processing Kakao callback, skipping...');
          return;
        }

        // 이미 로그인된 경우 마이페이지로 리다이렉트
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          console.log('User already logged in, redirecting to mypage');
          navigate('/mypage', { replace: true });
          return;
        }

        const searchParams = new URLSearchParams(window.location.search);
        const code = searchParams.get('code');
        const error = searchParams.get('error');
        const state = searchParams.get('state');
        const savedState = localStorage.getItem('kakao_state');

        console.log('Auth callback parameters:', {
          code: code ? `${code.substring(0, 10)}...` : null,
          error,
          state,
          savedState
        });

        // 이미 처리된 코드인지 확인
        if (code && processedCode.current === code) {
          console.log('Code already processed, skipping...');
          return;
        }

        // 상태 값 검증
        if (!state || !savedState || state !== savedState) {
          throw new Error('INVALID_STATE');
        }

        // 에러가 있거나 코드가 없는 경우
        if (error || !code) {
          const errorMessage = error 
            ? '카카오 로그인이 취소되었습니다.' 
            : '인증 코드를 찾을 수 없습니다.';

          logError('KakaoAuthHandler', new Error(error || 'Missing authorization code'));
          navigate('/login', {
            replace: true,
            state: { error: errorMessage }
          });
          return;
        }

        // 처리 중 상태 설정
        isProcessing.current = true;
        processedCode.current = code;

        // 로그인 처리
        await processKakaoLogin(code);
        
        // 상태 값 제거
        localStorage.removeItem('kakao_state');
        sessionStorage.removeItem('kakao_login_in_progress');
        
        // 로그인 성공 시 마이페이지로 이동
        navigate('/mypage', { replace: true });
      } catch (error: any) {
        // 로그인 진행 상태 제거
        sessionStorage.removeItem('kakao_login_in_progress');
        
        logError('KakaoAuthHandler', error);
        console.error('Kakao auth error:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });

        // 이미 로그인된 상태에서 400 에러가 발생한 경우 마이페이지로 리다이렉트
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (error?.response?.status === 400 && currentUser) {
          console.log('400 error but user is logged in, redirecting to mypage');
          navigate('/mypage', { replace: true });
          return;
        }

        // 특별한 에러 타입 처리
        let errorMessage = '로그인 중 오류가 발생했습니다. 다시 시도해주세요.';

        if (error.message === 'AUTH_CODE_EXPIRED') {
          errorMessage = '인증 코드가 만료되었습니다. 다시 로그인해주세요.';
        } else if (error.message === 'INVALID_STATE') {
          errorMessage = '보안 검증에 실패했습니다. 다시 시도해주세요.';
        } else if (error?.response?.status === 400) {
          // 400 에러이지만 사용자가 로그인되지 않은 경우
          errorMessage = '로그인 처리 중 오류가 발생했습니다. 다시 시도해주세요.';
        } else if (error?.response?.status === 401) {
          errorMessage = '인증이 만료되었습니다. 다시 로그인해주세요.';
        } else if (error?.response?.status === 500) {
          errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
        }

        // 에러 발생 시 모든 카카오 관련 데이터 정리
        localStorage.removeItem('kakao_state');
        localStorage.removeItem('kakao_access_token');
        localStorage.removeItem('kakao_refresh_token');
        localStorage.removeItem('kakao_id_token');
        sessionStorage.removeItem('kakao_login_in_progress');

        navigate('/login', {
          replace: true,
          state: { error: errorMessage }
        });
      } finally {
        // 처리 완료 후 상태 초기화
        isProcessing.current = false;
      }
    };

    handleKakaoCallback();
  }, [navigate]);

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
    >
      <CircularProgress />
    </Box>
  );
};

export default KakaoAuthHandler; 